/**
 * AI 生成台（P-D③/③b，蓝图 §4 + 竞品对齐）。全出血双栏：
 *  - 左：固定宽表单栏——图片(文生图/图生图/AI溶图) · 视频(文生/图生/参考生/编辑)、
 *    多模型下拉、参考素材槽（从资产库/主体拉取或本地上传）、提示词 + 结构化辅助标签、
 *    参数随模型自适应、实时积分预估；
 *  - 右：结果区占满剩余宽度，带 全部/图片/视频 筛选、卡片做大、多列自适应。
 * 复用 modelRegistry / generationProvider（mock，接真时挂 /api/v2）。视觉原创，不照搬竞品。
 */
import { useMemo, useRef, useState } from 'react';
import { Download, Film, Image as ImageIcon, Loader2, Music, Plus, Sparkles, Upload, X } from 'lucide-react';

import type { GenerationParams } from '../../model/canvas';
import {
  IMAGE_MODELS,
  VIDEO_MODELS,
  estimateImageCredit,
  estimateVideoCredit,
  findImageModel,
  findVideoModel,
  formatDiscount,
} from '../../model/modelRegistry';
import { PROMPT_ASSIST, appendTerm } from '../../model/promptAssist';
import { EMOTIONS } from '../../model/voice';
import { collectAssets } from '../../features/canvas/canvasLibrary';
import { generationProvider, type GeneratedImage, type GeneratedVideo } from '../../features/canvas/generationService';
import { Modal } from '../../ui/Modal';

const field = 'w-full rounded-control bg-surface-base px-2.5 py-2 text-ui text-ink-primary outline-none';
const sel = 'w-full rounded-control bg-surface-base px-2 py-1.5 text-uiSm text-ink-primary outline-none';
const seg = 'flex-1 rounded-control px-2 py-1.5 text-uiSm transition-colors';
const segOn = 'bg-state-info/15 font-semibold text-state-info';
const segOff = 'text-ink-secondary hover:text-ink-primary';
const MAX_REFS = 8;

const IMG_SUBS = [
  { k: 't2i', label: '文生图' },
  { k: 'i2i', label: '图生图' },
  { k: 'blend', label: 'AI溶图' },
] as const;
const VID_SUBS = [
  { k: 't2v', label: '文生视频' },
  { k: 'i2v', label: '图生视频' },
  { k: 'r2v', label: '参考生视频' },
  { k: 'edit', label: '视频编辑' },
] as const;
type ImgSub = (typeof IMG_SUBS)[number]['k'];
type VidSub = (typeof VID_SUBS)[number]['k'];
const NEEDS_REF = new Set<string>(['i2i', 'blend', 'i2v', 'r2v', 'edit']);

function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.readAsDataURL(file);
  });
}
function download(url: string, name: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
const isImageLike = (u: string): boolean => u.startsWith('data:image') || /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(u);

type Mode = 'image' | 'video' | 'voice';

export function GenerationStudioPage() {
  const [mode, setMode] = useState<Mode>('image');
  const [imgSub, setImgSub] = useState<ImgSub>('t2i');
  const [vidSub, setVidSub] = useState<VidSub>('t2v');
  const [prompt, setPrompt] = useState('');
  const [negative, setNegative] = useState('');
  const [refs, setRefs] = useState<string[]>([]);

  const [imgModelId, setImgModelId] = useState(IMAGE_MODELS[0]?.id ?? '');
  const [vidModelId, setVidModelId] = useState(VIDEO_MODELS[0]?.id ?? '');
  const [aspect, setAspect] = useState<GenerationParams['aspectRatio']>('1:1');
  const [resolution, setResolution] = useState('1080P');
  const [count, setCount] = useState(2);
  const [duration, setDuration] = useState(5);

  const [generating, setGenerating] = useState(false);
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [videos, setVideos] = useState<GeneratedVideo[]>([]);
  const [resultFilter, setResultFilter] = useState<'all' | 'image' | 'video' | 'voice'>('all');
  const [voiceText, setVoiceText] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [voiceEmotion, setVoiceEmotion] = useState<string>(EMOTIONS[0] ?? '中性');
  const [audios, setAudios] = useState<{ url: string; text: string; voiceId: string }[]>([]);
  const [assistOpen, setAssistOpen] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const assets = useMemo(() => (pickerOpen ? collectAssets() : { media: [], subjects: [] }), [pickerOpen]);
  const voices = useMemo(() => generationProvider.listVoices(), []);
  const effVoiceId = voiceId !== '' ? voiceId : (voices[0]?.id ?? '');

  const sub = mode === 'image' ? imgSub : vidSub;
  const needsRef = NEEDS_REF.has(sub);
  const imgModel = findImageModel(imgModelId) ?? IMAGE_MODELS[0];
  const vidModel = findVideoModel(vidModelId) ?? VIDEO_MODELS[0];
  const model = mode === 'image' ? imgModel : vidModel;
  const aspects = model?.aspectRatios ?? [];
  const resolutions = model?.resolutions ?? [];
  const effAspect = aspects.includes(aspect) ? aspect : (aspects[0] ?? '1:1');
  const effResolution = resolutions.includes(resolution) ? resolution : (resolutions[0] ?? '1080P');
  const effCount = mode === 'image' && imgModel ? Math.min(count, imgModel.maxCount) : count;
  const effDuration = mode === 'video' && vidModel ? (vidModel.durations.includes(duration) ? duration : (vidModel.durations[0] ?? 5)) : duration;
  const cost =
    mode === 'image' && imgModel
      ? estimateImageCredit(imgModel, effResolution, effCount)
      : mode === 'video' && vidModel
        ? estimateVideoCredit(vidModel, effResolution, effDuration)
        : 0;

  function pickModel(id: string): void {
    if (mode === 'image') {
      setImgModelId(id);
      const m = findImageModel(id);
      if (m !== undefined) {
        if (!m.resolutions.includes(resolution)) setResolution(m.resolutions[0] ?? '1080P');
        if (!m.aspectRatios.includes(aspect)) setAspect(m.aspectRatios[0] ?? '1:1');
      }
    } else {
      setVidModelId(id);
      const m = findVideoModel(id);
      if (m !== undefined) {
        if (!m.resolutions.includes(resolution)) setResolution(m.resolutions[0] ?? '1080P');
        if (!m.aspectRatios.includes(aspect)) setAspect(m.aspectRatios[0] ?? '16:9');
      }
    }
  }
  function addRef(url: string): void {
    setRefs((prev) => (prev.includes(url) || prev.length >= MAX_REFS ? prev : [...prev, url]));
  }
  function removeRef(url: string): void {
    setRefs((prev) => prev.filter((u) => u !== url));
  }
  async function onUploadFiles(files: FileList | null): Promise<void> {
    if (files === null) {
      return;
    }
    for (const f of Array.from(files)) {
      if (refs.length >= MAX_REFS) {
        break;
      }
      addRef(await readDataUrl(f));
    }
  }

  async function runGenerate(): Promise<void> {
    if (prompt.trim() === '' || generating) {
      return;
    }
    setGenerating(true);
    try {
      if (mode === 'image') {
        const res = await generationProvider.generateImage({ prompt, aspectRatio: effAspect, resolution: effResolution, count: effCount });
        setImages((prev) => [...res, ...prev]);
      } else {
        const v = await generationProvider.generateVideo({ prompt, aspectRatio: effAspect, resolution: effResolution, durationSec: effDuration });
        setVideos((prev) => [v, ...prev]);
      }
    } finally {
      setGenerating(false);
    }
  }

  async function runTts(): Promise<void> {
    if (voiceText.trim() === '' || generating) {
      return;
    }
    setGenerating(true);
    try {
      const r = await generationProvider.tts({ text: voiceText, voiceId: effVoiceId, emotion: voiceEmotion });
      setAudios((prev) => [{ url: r.url, text: voiceText, voiceId: effVoiceId }, ...prev]);
    } finally {
      setGenerating(false);
    }
  }

  const models = mode === 'image' ? IMAGE_MODELS : VIDEO_MODELS;
  const showImages = resultFilter === 'all' || resultFilter === 'image';
  const showVideos = resultFilter === 'all' || resultFilter === 'video';
  const showAudios = resultFilter === 'all' || resultFilter === 'voice';
  const resultCount = (showImages ? images.length : 0) + (showVideos ? videos.length : 0) + (showAudios ? audios.length : 0);

  return (
    <div className="flex h-full overflow-hidden">
      {/* 左：表单栏 */}
      <aside className="flex w-[440px] shrink-0 flex-col gap-3 overflow-y-auto border-r border-surface-line bg-surface-raised/60 p-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">AI 生成</h1>
          <p className="mt-0.5 text-uiSm text-ink-muted">选模式与模型、填提示词、拉参考，即时出图出片（mock，接真后出真效果）。</p>
        </div>

        {/* 图片/视频 */}
        <div className="flex gap-1.5">
          {(
            [
              { k: 'image', label: '图片生成', Icon: ImageIcon },
              { k: 'video', label: '视频生成', Icon: Film },
              { k: 'voice', label: '智能配音', Icon: Music },
            ] as { k: Mode; label: string; Icon: typeof ImageIcon }[]
          ).map((t) => (
            <button key={t.k} type="button" onClick={() => setMode(t.k)} className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-control px-3 py-2 text-ui transition-colors ${mode === t.k ? 'bg-accent/15 font-semibold text-accent' : 'bg-surface-base text-ink-secondary hover:text-ink-primary'}`}>
              <t.Icon size={15} />
              {t.label}
            </button>
          ))}
        </div>

        {mode !== 'voice' && (
          <>
        {/* 子模式 */}
        <div className="flex gap-1 rounded-control bg-surface-base p-1">
          {(mode === 'image' ? IMG_SUBS : VID_SUBS).map((s) => {
            const on = (mode === 'image' ? imgSub : vidSub) === s.k;
            return (
              <button key={s.k} type="button" onClick={() => (mode === 'image' ? setImgSub(s.k as ImgSub) : setVidSub(s.k as VidSub))} className={`${seg} ${on ? segOn : segOff}`}>
                {s.label}
              </button>
            );
          })}
        </div>

        {/* 模型 */}
        <label className="flex flex-col gap-1">
          <span className="text-uiSm text-ink-faint">模型</span>
          <select className={sel} value={mode === 'image' ? imgModelId : vidModelId} onChange={(e) => pickModel(e.target.value)}>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
                {m.discount < 1 ? ` · ${formatDiscount(m.discount)}` : ''}
              </option>
            ))}
          </select>
          {model?.note !== undefined && <span className="text-uiSm text-ink-faint">{model.note}</span>}
        </label>

        {/* 参考素材槽 */}
        {needsRef && (
          <div className="flex flex-col gap-1.5">
            <span className="text-uiSm text-ink-faint">参考素材（从资产/主体拉取或本地上传 · {refs.length}/{MAX_REFS}）</span>
            <div className="flex flex-wrap gap-2">
              {refs.map((u) => (
                <div key={u} className="group relative h-16 w-16 overflow-hidden rounded-control border border-surface-line bg-surface-base">
                  {isImageLike(u) ? <img src={u} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-ink-faint">{u.startsWith('data:audio') ? <Music size={18} /> : <Film size={18} />}</span>}
                  <button type="button" aria-label="移除" onClick={() => removeRef(u)} className="absolute right-0.5 top-0.5 rounded bg-black/60 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
                    <X size={11} />
                  </button>
                </div>
              ))}
              {refs.length < MAX_REFS && (
                <>
                  <button type="button" onClick={() => setPickerOpen(true)} className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-control border border-dashed border-surface-line text-uiSm text-ink-faint transition-colors hover:border-accent hover:text-accent">
                    <Plus size={16} />
                    从资产
                  </button>
                  <button type="button" onClick={() => fileRef.current?.click()} className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-control border border-dashed border-surface-line text-uiSm text-ink-faint transition-colors hover:border-accent hover:text-accent">
                    <Upload size={16} />
                    本地传
                  </button>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*,video/*,audio/*" multiple className="hidden" onChange={(e) => { void onUploadFiles(e.target.files); e.currentTarget.value = ''; }} />
          </div>
        )}

        {/* 提示词 + 结构化辅助 */}
        <label className="flex flex-col gap-1">
          <span className="text-uiSm text-ink-faint">提示词</span>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={needsRef ? '结合参考图描述想要的内容…' : '描述你想生成的画面…'} className={`${field} h-36 resize-y overflow-y-auto leading-relaxed`} />
          <div className="relative flex flex-wrap gap-1.5 pt-1">
            {PROMPT_ASSIST.map((g) => (
              <div key={g.key} className="relative">
                <button type="button" onClick={() => setAssistOpen((v) => (v === g.key ? null : g.key))} className={`rounded-control border px-2 py-0.5 text-uiSm transition-colors ${assistOpen === g.key ? 'border-accent text-accent' : 'border-surface-line text-ink-secondary hover:text-ink-primary'}`}>
                  {g.label}
                </button>
                {assistOpen === g.key && (
                  <div className="absolute left-0 top-full z-20 mt-1 flex w-52 flex-wrap gap-1 rounded-card border border-surface-edge bg-surface-overlay p-2 shadow-[0_6px_20px_rgba(0,0,0,.5)]">
                    {g.terms.map((term) => (
                      <button key={term} type="button" onClick={() => { setPrompt((p) => appendTerm(p, term)); setAssistOpen(null); }} className="rounded-control bg-surface-base px-2 py-0.5 text-uiSm text-ink-secondary transition-colors hover:text-accent">
                        {term}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </label>

        {mode === 'image' && imgModel?.supportsNegative && (
          <label className="flex flex-col gap-1">
            <span className="text-uiSm text-ink-faint">负向提示词</span>
            <textarea value={negative} onChange={(e) => setNegative(e.target.value)} placeholder="不想出现的元素…" className={`${field} h-24 resize-y overflow-y-auto leading-relaxed`} />
          </label>
        )}

        {/* 参数 */}
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-uiSm text-ink-faint">画幅</span>
            <select className={sel} value={effAspect} onChange={(e) => setAspect(e.target.value as GenerationParams['aspectRatio'])}>
              {aspects.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-uiSm text-ink-faint">分辨率</span>
            <select className={sel} value={effResolution} onChange={(e) => setResolution(e.target.value)}>
              {resolutions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
          {mode === 'image' ? (
            <label className="flex flex-col gap-1">
              <span className="text-uiSm text-ink-faint">张数</span>
              <select className={sel} value={effCount} onChange={(e) => setCount(Number(e.target.value))}>
                {Array.from({ length: imgModel?.maxCount ?? 4 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="flex flex-col gap-1">
              <span className="text-uiSm text-ink-faint">时长(秒)</span>
              <select className={sel} value={effDuration} onChange={(e) => setDuration(Number(e.target.value))}>
                {(vidModel?.durations ?? [5, 10]).map((d) => (
                  <option key={d} value={d}>{d}s</option>
                ))}
              </select>
            </label>
          )}
        </div>

        <button type="button" className="btn-primary mt-auto inline-flex items-center justify-center gap-1.5 py-2.5" disabled={generating || prompt.trim() === ''} onClick={() => void runGenerate()}>
          {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {generating ? '生成中…' : sub === 'blend' ? '生成溶图' : mode === 'image' ? '生成图片' : '生成视频'}
          <span className="ml-1 rounded bg-black/20 px-1.5 text-uiSm">≈{cost} 积分</span>
        </button>
          </>
        )}

        {mode === 'voice' && (
          <>
            <label className="flex flex-col gap-1">
              <span className="text-uiSm text-ink-faint">台词文本</span>
              <textarea value={voiceText} onChange={(e) => setVoiceText(e.target.value)} placeholder="输入要配音的文本…" className={`${field} h-36 resize-y overflow-y-auto leading-relaxed`} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-uiSm text-ink-faint">音色</span>
              <select className={sel} value={effVoiceId} onChange={(e) => setVoiceId(e.target.value)}>
                {voices.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
              <span className="text-uiSm text-ink-faint">文字造声 / 上传有权克隆：在角色卡“选择音色”的音色库中管理</span>
            </label>
            <div className="flex flex-col gap-1">
              <span className="text-uiSm text-ink-faint">情绪</span>
              <div className="flex flex-wrap gap-1.5">
                {EMOTIONS.map((em) => (
                  <button key={em} type="button" onClick={() => setVoiceEmotion(em)} className={`rounded-control px-2.5 py-1 text-uiSm transition-colors ${voiceEmotion === em ? 'bg-accent/15 font-semibold text-accent' : 'bg-surface-base text-ink-secondary hover:text-ink-primary'}`}>{em}</button>
                ))}
              </div>
            </div>
            <button type="button" className="btn-primary mt-auto inline-flex items-center justify-center gap-1.5 py-2.5" disabled={generating || voiceText.trim() === ''} onClick={() => void runTts()}>
              {generating ? <Loader2 size={15} className="animate-spin" /> : <Music size={15} />}
              {generating ? '合成中…' : '生成配音'}
            </button>
          </>
        )}
      </aside>

      {/* 右：结果区 */}
      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto p-6">
        <div className="mb-4 flex items-center gap-1.5">
          {(['all', 'image', 'video', 'voice'] as const).map((f) => (
            <button key={f} type="button" onClick={() => setResultFilter(f)} className={`rounded-control px-3 py-1 text-uiSm transition-colors ${resultFilter === f ? 'bg-accent/15 font-semibold text-accent' : 'text-ink-secondary hover:text-ink-primary'}`}>
              {f === 'all' ? '全部' : f === 'image' ? '图片' : f === 'video' ? '视频' : '配音'}
            </button>
          ))}
          {resultCount > 0 && <span className="ml-1 text-uiSm text-ink-faint">{resultCount} 项</span>}
        </div>
        {resultCount === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-card border border-dashed border-surface-line text-center">
            <ImageIcon size={30} className="text-ink-faint" />
            <p className="text-uiSm text-ink-muted">生成的作品会展示在这里。左侧选模式、填提示词、拉参考后点「生成」。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {showImages &&
              images.map((im, i) => (
                <figure key={`i-${i}`} className="panel group relative overflow-hidden">
                  <img src={im.url} alt="" className="block aspect-square w-full object-cover" />
                  <button type="button" aria-label="下载" onClick={() => download(im.url, `image-${i}.png`)} className="absolute right-2 top-2 rounded-control bg-black/55 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
                    <Download size={15} />
                  </button>
                </figure>
              ))}
            {showVideos &&
              videos.map((v, i) => (
                <figure key={`v-${i}`} className="panel group relative overflow-hidden">
                  <img src={v.thumbnail} alt="" className="block aspect-video w-full object-cover" />
                  <span className="absolute bottom-2 left-2 rounded bg-black/60 px-1.5 text-[11px] text-white">{v.durationSec}s</span>
                  <button type="button" aria-label="下载" onClick={() => download(v.url, `video-${i}.mp4`)} className="absolute right-2 top-2 rounded-control bg-black/55 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
                    <Download size={15} />
                  </button>
                </figure>
              ))}
            {showAudios &&
              audios.map((a, i) => (
                <figure key={`a-${i}`} className="panel group flex flex-col gap-2 p-3">
                  <div className="flex items-center gap-2 text-uiSm text-ink-secondary">
                    <Music size={15} className="text-accent" />
                    <span className="truncate">{a.text}</span>
                    <button type="button" aria-label="下载" onClick={() => download(a.url, `voice-${i}.wav`)} className="ml-auto shrink-0 text-ink-faint hover:text-accent">
                      <Download size={15} />
                    </button>
                  </div>
                  <audio controls src={a.url} className="w-full" />
                </figure>
              ))}
          </div>
        )}
      </main>

      {/* 从资产库选参考 */}
      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="从资产库选择参考"
        summary="跨画布已生成的素材，点选加入参考（最多 8 项）。"
        footer={
          <button type="button" className="btn-primary px-3 py-1.5 text-ui" onClick={() => setPickerOpen(false)}>
            完成
          </button>
        }
      >
        {assets.media.length === 0 ? (
          <p className="py-8 text-center text-uiSm text-ink-muted">资产库暂无素材。先在画布生成图片或视频，或用「本地传」上传。</p>
        ) : (
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {assets.media.map((m) => {
              const on = refs.includes(m.url);
              return (
                <button key={`${m.canvasId}-${m.nodeId}`} type="button" onClick={() => (on ? removeRef(m.url) : addRef(m.url))} className={`relative aspect-square overflow-hidden rounded-control border-2 ${on ? 'border-accent' : 'border-transparent'} bg-surface-base`}>
                  {m.thumbnail !== undefined || isImageLike(m.url) ? <img src={m.thumbnail ?? m.url} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-ink-faint">{m.kind === 'audio' ? <Music size={18} /> : <Film size={18} />}</span>}
                  {on && <span className="absolute right-1 top-1 rounded-full bg-accent px-1 text-[10px] font-bold text-accent-ink">✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}
